import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Globe2, RotateCw, PhoneIncoming, MessageSquare, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { lrTemplates, lrTemplateUpsert, lrTemplateRender, lrMissedCallbacks, lrMissedCallbackAdvance } from "@/lib/api";

const LANGS = [
  { key: "en", label: "English" }, { key: "es", label: "Spanish" },
  { key: "fr", label: "French" }, { key: "hi", label: "Hindi" },
  { key: "ar", label: "Arabic" },
];

export default function AdminLrTemplates() {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState({});
  const [selectedKey, setSelectedKey] = useState("lr.speed.call");
  const [selectedLang, setSelectedLang] = useState("en");
  const [form, setForm] = useState({ speak: "", body: "", subject: "" });
  const [preview, setPreview] = useState(null);
  const [callbacks, setCallbacks] = useState([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setError("");
    try {
      const [t, cb] = await Promise.all([lrTemplates(), lrMissedCallbacks()]);
      setTemplates(t.templates || {}); setCallbacks(cb.items || []);
    } catch (e) { setError(e?.response?.data?.detail || "Admin/editor sign-in required."); }
  };
  useEffect(() => { document.title = "AI Voice Templates | Admin"; refresh(); }, []);
  useEffect(() => {
    const t = templates[selectedKey]?.[selectedLang] || {};
    setForm({ speak: t.speak || "", body: t.body || "", subject: t.subject || "" });
    setPreview(null);
  }, [selectedKey, selectedLang, templates]);

  const save = async () => {
    setBusy(true); setNotice("");
    try {
      await lrTemplateUpsert({
        key: selectedKey, language: selectedLang,
        speak: form.speak || null, body: form.body || null, subject: form.subject || null,
      });
      setNotice(`Saved override for ${selectedKey} · ${selectedLang}.`);
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  const doPreview = async () => {
    const r = await lrTemplateRender({
      key: selectedKey, language: selectedLang,
      fields: { business: "Bright Path Dental", first_name: "Ana", link: "https://example.com/book" },
    });
    setPreview(r);
  };
  const advance = async (cid, outcome) => {
    try { await lrMissedCallbackAdvance(cid, outcome); refresh(); }
    catch (e) { setError(e?.response?.data?.detail || "Advance failed."); }
  };

  const keys = Object.keys(templates);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="admin-lr-templates">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/admin" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Admin</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-3">
            <Globe2 className="w-8 h-8 text-indigo-600" /> Multilingual voice + missed-call callbacks
          </h1>
          <p className="mt-2 text-slate-600">Edit per-language templates that the AI uses on outbound calls, SMS and email. Language is auto-detected from the caller's profile or phone country code (Spanish/French/Hindi/Arabic all supported at launch).</p>

          {error && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{error}</div>}
          {notice && <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">{notice}</div>}

          <div className="mt-6 border-b border-slate-200 flex gap-2 text-sm">
            <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testId="admin-lr-tab-tpl">Templates</TabBtn>
            <TabBtn active={tab === "callbacks"} onClick={() => setTab("callbacks")} testId="admin-lr-tab-cb">Missed-call callbacks</TabBtn>
          </div>

          {tab === "templates" && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Sidebar: keys */}
              <aside className="rounded-2xl bg-white border border-slate-200 p-3 space-y-1" data-testid="admin-lr-tpl-list">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold p-2">Templates</div>
                {keys.map((k) => (
                  <button key={k} onClick={() => setSelectedKey(k)} data-testid={`admin-lr-tpl-key-${k}`}
                    className={`w-full text-left rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      selectedKey === k ? "bg-indigo-600 text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}>
                    <div className="flex items-center gap-2">
                      {k.includes("call") ? <PhoneIncoming className="w-3.5 h-3.5" /> :
                        k.includes("sms") ? <MessageSquare className="w-3.5 h-3.5" /> :
                        <Mail className="w-3.5 h-3.5" />}
                      <span className="text-xs">{k}</span>
                    </div>
                  </button>
                ))}
              </aside>

              {/* Editor */}
              <section className="md:col-span-2 rounded-2xl bg-white border border-slate-200 p-5" data-testid="admin-lr-tpl-editor">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-lg font-bold text-slate-900">{selectedKey}</div>
                  <div className="flex flex-wrap gap-1">
                    {LANGS.map((l) => (
                      <button key={l.key} onClick={() => setSelectedLang(l.key)}
                        data-testid={`admin-lr-tpl-lang-${l.key}`}
                        className={`text-xs px-3 py-1 rounded-full font-semibold border transition ${
                          selectedLang === l.key ? "bg-indigo-600 text-white border-indigo-700"
                                                       : "bg-white text-slate-700 border-slate-300 hover:border-indigo-400"
                        }`}>{l.label}</button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {selectedKey.includes("call") || selectedKey.includes("callback") ? (
                    <Field label="Spoken script (voice)">
                      <textarea value={form.speak} rows={3}
                        onChange={(e) => setForm({ ...form, speak: e.target.value })}
                        data-testid="admin-lr-tpl-speak"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
                    </Field>
                  ) : null}
                  {selectedKey.includes("sms") || selectedKey.includes("email") ? (
                    <>
                      {selectedKey.includes("email") && (
                        <Field label="Subject">
                          <input value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            data-testid="admin-lr-tpl-subject"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                        </Field>
                      )}
                      <Field label="Body">
                        <textarea value={form.body} rows={4}
                          onChange={(e) => setForm({ ...form, body: e.target.value })}
                          data-testid="admin-lr-tpl-body"
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
                      </Field>
                    </>
                  ) : null}
                  <div className="text-xs text-slate-500">Placeholders: <code>{"{business}"}</code>, <code>{"{first_name}"}</code>, <code>{"{link}"}</code></div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={save} disabled={busy} data-testid="admin-lr-tpl-save"
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white px-5 py-2.5 font-semibold text-sm transition">
                    <Save className="w-4 h-4" /> Save override
                  </button>
                  <button onClick={doPreview} data-testid="admin-lr-tpl-preview"
                    className="inline-flex items-center gap-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-5 py-2.5 font-semibold text-sm transition">
                    Preview render
                  </button>
                </div>
                {preview && (
                  <div className="mt-4 rounded-xl bg-slate-950 text-white p-4" data-testid="admin-lr-tpl-preview-out">
                    <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Preview · {preview.language} · {preview.source}</div>
                    {preview.speak && <div className="mt-2 text-sm"><strong>Speak:</strong> {preview.speak}</div>}
                    {preview.subject && <div className="mt-2 text-sm"><strong>Subject:</strong> {preview.subject}</div>}
                    {preview.body && <div className="mt-2 text-sm whitespace-pre-wrap"><strong>Body:</strong> {preview.body}</div>}
                  </div>
                )}
              </section>
            </div>
          )}

          {tab === "callbacks" && (
            <section className="mt-6 rounded-2xl bg-white border border-slate-200 p-3" data-testid="admin-lr-cb-list">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold p-2">Missed-call AI callbacks ({callbacks.length})</div>
              <div className="space-y-2 max-h-[640px] overflow-y-auto">
                {callbacks.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No missed-call callbacks yet.</div>}
                {callbacks.map((cb) => (
                  <div key={cb.id} className="rounded-lg border border-slate-200 p-3" data-testid={`admin-lr-cb-${cb.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <div className="text-sm font-bold text-slate-900 font-mono">{cb.caller_number}</div>
                        <div className="mt-0.5 text-[11px] text-slate-500 uppercase tracking-widest">
                          {cb.status} · {cb.language} · attempts {cb.attempts}/{cb.cadence?.length}
                        </div>
                        {cb.last_outcome && <div className="mt-1 text-xs text-slate-600">Last outcome: {cb.last_outcome}</div>}
                      </div>
                      <div className="flex gap-2">
                        {["answered", "booked", "no_answer", "voicemail", "opted_out"].map((o) => (
                          <button key={o} onClick={() => advance(cb.id, o)}
                            data-testid={`admin-lr-cb-adv-${cb.id}-${o}`}
                            disabled={cb.status === "completed" || cb.status === "exhausted"}
                            className="text-xs uppercase tracking-wide font-bold border border-slate-300 rounded px-2 py-1 hover:border-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed">
                            {o}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (<label className="block"><div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>{children}</label>);
}
function TabBtn({ active, onClick, children, testId }) {
  return <button onClick={onClick} data-testid={testId}
    className={`px-3 py-2 border-b-2 font-semibold ${active ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{children}</button>;
}
